import { ampli } from '@/ampli';
import { keepPreviousData } from '@tanstack/react-query';
import { useQueries } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce, useIntersection, useMedia } from 'react-use';

import { chainAvatarRadius } from '@/ui/Avatars/ChainAvatar';
import { AlertIcon } from '@/ui/Icons/AlertIcon';
import { GlobeIcon } from '@/ui/Icons/GlobeIcon';

import {
  NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID,
  SUPPORTED_CHAIN_IDS,
} from '../../../constants/chain';
import { useDiscoverAvailablePages } from '../../../hooks/homepage/useDiscoverAvailablePages';
import { useDiscoveryStickyHeader } from '../../../hooks/homepage/useDiscoveryStickyHeader';
import {
  useDiscoveryTable,
  useDiscoveryTablePrefetch,
} from '../../../hooks/homepage/useDiscoveryTable';
import { tokenEntityQueryOptions } from '../../../hooks/tokenQueries/tokenEntityCache';
import { useFiatDisplay } from '../../../hooks/useFiatDisplay';
import { useLiquidityScore } from '../../../hooks/useLiquidityScore';
import { fontStyles } from '../../../styles/typography';
import { breakpoints } from '../../../styles/util';
import { Pagination } from '../../../ui/Pagination';
import type { TokenResult } from '../../../utils/0x/token-registry.types';
import { classNames } from '../../../utils/classnames';
import { addMetaClickHandlers } from '../../../utils/components';
import type {
  Asset,
  DiscoveryParametersDeserialized,
  DiscoveryParametersSerialized,
  DiscoveryPreset,
  DiscoveryTokenResult,
  UnifiedDiscoveryResultItem,
} from '../../../utils/discovery/types';
import {
  isNeonDbTokensBackedPreset,
  isUnifiedDiscoveryPreset,
} from '../../../utils/discovery/types';
import { isAsset } from '../../../utils/discovery/types';
import { tokenKey } from '../../../utils/normalizeAddress';
import { routes } from '../../../utils/routes';
import { AvatarSkeleton, TextSkeleton } from '../../Skeleton';
import {
  CATEGORY_COLUMNS_MAP,
  UNIFIED_CURRENCIES_COLUMNS,
  UNIFIED_COMMODITIES_COLUMNS,
  UNIFIED_POPULAR_COLUMNS,
  UNIFIED_STOCKS_COLUMNS,
} from './Columns/DiscoveryColumnDefinitions';
import { DiscoveryTableHeader } from './DiscoveryTableHeader';
import { EmptyState } from './EmptyState';
import { STICKY_FIRST_CELL_BG_CLASSES } from './stickyFirstCell';
import { UnifiedTokenRow } from './UnifiedTokenRow';

const MAX_PAGE_SECTION_SIZE = 9;

// Per-preset placeholder copy for the token search input. Most presets follow
// the "Search {label} tokens" pattern, but a handful read awkwardly that way
// (e.g., "Search currencies tokens", "Search stocks tokens"), so those are
// hand-tuned to drop the redundant "tokens" or rephrase. Falls back to the
// generic "Search tokens" if no preset is active.
const SEARCH_PLACEHOLDER_BY_PRESET: Record<DiscoveryPreset, string> = {
  popular: 'Search popular tokens',
  trending: 'Search trending tokens',
  wrapped: 'Search wrapped tokens',
  social: 'Search social tokens',
  lsts: 'Search LSTs',
  launchpads: 'Search launchpad tokens',
  currencies: 'Search currencies',
  stocks: 'Search stocks',
  commodities: 'Search commodities',
};

const DEFAULT_SEARCH_PLACEHOLDER = 'Search tokens';

const getSearchPlaceholder = (preset: DiscoveryPreset | undefined) =>
  (preset && SEARCH_PLACEHOLDER_BY_PRESET[preset]) ?? DEFAULT_SEARCH_PLACEHOLDER;

export const buildTokenPageRoute = (token: DiscoveryTokenResult['token']) => {
  const { chainId, address } = token;

  return {
    pathname: routes.TOKEN_PROFILE,
    query: {
      chainId: NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[chainId],
      contractAddresses: [address],
    },
  };
};

type Params = {
  filters: DiscoveryParametersDeserialized['filters'];
  sortBy: DiscoveryParametersSerialized['sortBy'];
  sortDirection: DiscoveryParametersSerialized['sortDirection'];
  setSorting: (sorting: {
    sortBy: DiscoveryParametersSerialized['sortBy'];
    sortDirection: DiscoveryParametersSerialized['sortDirection'];
  }) => void;
  page: number;
  setPage: (page: number) => void;
  timeframe: DiscoveryParametersSerialized['timeframe'];
  preset?: DiscoveryParametersSerialized['preset'];
  showStocksAvailabilityBanner: boolean;
  columnsOverride?: ColumnDef<DiscoveryTokenResult>[];
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
  /**
   * When true, the sticky header overlay is not rendered. Useful on pages
   * (like the Solana page) where the table is laid out in a narrow column
   * alongside other content, so a viewport-spanning sticky overlay would
   * look misaligned.
   */
  disableStickyHeader?: boolean;
};

const SEARCH_DEBOUNCE_MS = 300;

export function DiscoveryTable({
  filters: filtersProp,
  timeframe,
  sortBy,
  sortDirection,
  setSorting,
  page,
  setPage,
  preset,
  showStocksAvailabilityBanner,
  columnsOverride,
  searchQuery = '',
  onSearchQueryChange,
  disableStickyHeader = false,
}: Params) {
  const router = useRouter();
  const { formatFiat, currencySymbol, convertFromUsd } = useFiatDisplay();
  const prefetch = useDiscoveryTablePrefetch();
  const isTabletUp = useMedia(breakpoints.tablet, false);

  const stickyHeaderTriggerRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const stickyHeaderScrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const isSyncingHorizontalScrollRef = useRef(false);

  // Local state drives the controlled search input so typing feels instant.
  // The debounced value is what gets lifted up to the parent (and ultimately into
  // the request query key) so the API is not hit on every keystroke.
  const [searchInputValue, setSearchInputValue] = useState(searchQuery);
  // The same input is rendered in two separate <table>s (the regular header and
  // the sticky overlay), so each gets its own ref. We focus whichever one is
  // currently on screen.
  const regularSearchInputRef = useRef<HTMLInputElement>(null);
  const stickySearchInputRef = useRef<HTMLInputElement>(null);
  // Dedupes the discovery search ampli event so refetches and revalidations
  // for the same (preset, query, hadNetworkFilter) tuple do not re-fire.
  const lastFiredSearchKeyRef = useRef<string | null>(null);

  useDebounce(
    () => {
      if (searchInputValue !== searchQuery) {
        onSearchQueryChange?.(searchInputValue);
      }
    },
    SEARCH_DEBOUNCE_MS,
    [searchInputValue],
  );

  // Keep the local input in sync when the parent resets the query externally
  // (for example, when switching categories).
  useEffect(() => {
    setSearchInputValue((previous) => (previous === searchQuery ? previous : searchQuery));
  }, [searchQuery]);

  const handleClearSearch = useCallback(() => {
    setSearchInputValue('');
    onSearchQueryChange?.('');
    // Focus whichever input is currently visible. The sticky overlay's
    // visibility is tracked imperatively via `data-visible` by
    // `useDiscoveryStickyHeader`, so we read it directly here.
    const isStickyVisible = stickyHeaderRef.current?.dataset.visible === 'true';
    const targetRef = isStickyVisible ? stickySearchInputRef : regularSearchInputRef;
    targetRef.current?.focus();
  }, [onSearchQueryChange]);

  const pageInfo = useMemo(() => {
    return {
      pageIndex: page - 1,
      pageSize: 50,
    };
  }, [page]);

  const [maxPageSectionSize, setMaxPageSectionSize] = useState(MAX_PAGE_SECTION_SIZE);
  const [isScrollable, setIsScrollable] = useState(false);

  const { topOffset: stickyHeaderTopOffset } = useDiscoveryStickyHeader({
    triggerRef: stickyHeaderTriggerRef,
    scrollContainerRef,
    stickyHeaderRef,
    stickyScrollContainerRef: stickyHeaderScrollContainerRef,
    enabled: !disableStickyHeader,
  });

  // Update network filter to include all supported chains when empty
  const filters = useMemo(
    () => ({
      ...filtersProp,
      networks: filtersProp.networks.length
        ? filtersProp.networks
        : SUPPORTED_CHAIN_IDS.map(Number),
    }),
    [filtersProp],
  );

  useEffect(() => {
    // set up handlers
    const stickyHeaderScrollContainer = stickyHeaderScrollContainerRef.current;
    const scrollContainer = scrollContainerRef.current;
    const content = contentRef.current;
    const scrollbar = scrollbarRef.current;
    const thumb = thumbRef.current;

    const setDidScrollState = (didScroll: boolean) => {
      const didScrollValue = didScroll ? 'true' : 'false';

      scrollContainer?.setAttribute('data-did-scroll', didScrollValue);
      stickyHeaderScrollContainer?.setAttribute('data-did-scroll', didScrollValue);
    };

    const syncHorizontalScroll = (source: HTMLDivElement, target: HTMLDivElement | null) => {
      if (!target || source.scrollLeft === target.scrollLeft) {
        return;
      }

      isSyncingHorizontalScrollRef.current = true;
      target.scrollLeft = source.scrollLeft;

      requestAnimationFrame(() => {
        isSyncingHorizontalScrollRef.current = false;
      });
    };

    const updateThumbPosition = () => {
      if (!scrollContainer || !content || !scrollbar || !thumb) {
        return;
      }

      const scrollbarWidth = scrollbar.clientWidth;
      const containerWidth = scrollContainer.clientWidth;
      const contentWidth = content.scrollWidth;
      const maxScroll = contentWidth - containerWidth;

      if (maxScroll <= 0) {
        thumb.style.left = '0px';
        return;
      }

      const thumbWidth = scrollbarWidth * (containerWidth / contentWidth);
      const scrollLeft = scrollContainer.scrollLeft;
      const maxThumbLeft = scrollbarWidth - thumbWidth;
      const thumbLeft = (scrollLeft / maxScroll) * maxThumbLeft;

      thumb.style.left = `${thumbLeft}px`;
    };

    const handleBodyScroll = () => {
      if (!scrollContainer) {
        return;
      }

      setDidScrollState(scrollContainer.scrollLeft > 0);

      if (isSyncingHorizontalScrollRef.current) {
        return;
      }

      syncHorizontalScroll(scrollContainer, stickyHeaderScrollContainer);
      updateThumbPosition();
    };

    const handleStickyHeaderScroll = () => {
      if (!stickyHeaderScrollContainer) {
        return;
      }

      setDidScrollState(stickyHeaderScrollContainer.scrollLeft > 0);

      if (isSyncingHorizontalScrollRef.current) {
        return;
      }

      syncHorizontalScroll(stickyHeaderScrollContainer, scrollContainer);
      updateThumbPosition();
    };

    const updateThumbSize = () => {
      if (scrollContainer && content && scrollbar && thumb) {
        const scrollbarWidth = scrollbar.clientWidth;
        const containerWidth = scrollContainer.clientWidth;
        const contentWidth = content.scrollWidth;
        const thumbWidth =
          contentWidth > 0 ? scrollbarWidth * (containerWidth / contentWidth) : scrollbarWidth;

        thumb.style.width = `${thumbWidth}px`;
      }
    };

    const handleResize = () => {
      if (window.matchMedia(breakpoints.desktop).matches) {
        setMaxPageSectionSize(MAX_PAGE_SECTION_SIZE);
      } else if (window.matchMedia(breakpoints.tablet).matches) {
        setMaxPageSectionSize(6);
      } else {
        setMaxPageSectionSize(2);
      }

      if (scrollContainer && content) {
        setIsScrollable(content.scrollWidth > scrollContainer.clientWidth);
      }

      if (scrollContainer) {
        syncHorizontalScroll(scrollContainer, stickyHeaderScrollContainer);
      }

      updateThumbSize();
      updateThumbPosition();
    };

    // passive means that we guarantee that we will not prevent the default behavior of the scroll event
    // this ensures that the event handler will be called as soon as possible
    scrollContainer?.addEventListener('scroll', handleBodyScroll, { passive: true });
    stickyHeaderScrollContainer?.addEventListener('scroll', handleStickyHeaderScroll, {
      passive: true,
    });
    window.addEventListener('resize', handleResize, { passive: true });

    function initialize() {
      if (!scrollContainer || !content) {
        // check again in 50ms
        setTimeout(initialize, 50);
        return;
      }

      setDidScrollState(scrollContainer.scrollLeft > 0);
      handleResize();
    }

    // initialize
    setTimeout(() => {
      initialize();
    }, 50);

    return () => {
      scrollContainer?.removeEventListener('scroll', handleBodyScroll);
      stickyHeaderScrollContainer?.removeEventListener('scroll', handleStickyHeaderScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 0-indexed
  const [highestKnownPage, setHighestKnownPage] = useState(1);

  const trimmedSearchQuery = searchQuery.trim();
  const effectiveSearchQuery = trimmedSearchQuery.length > 0 ? trimmedSearchQuery : undefined;

  const { data, error, isFetching } = useDiscoveryTable({
    page,
    sortBy: sortBy || 'trendingScore',
    sortDirection: sortDirection || 'desc',
    timeframe,
    filters,
    preset,
    pageDiscovery: false,
    searchQuery: effectiveSearchQuery,
    query: {
      placeholderData: keepPreviousData,
      // Searches are ephemeral so we don't want to serve stale cached results
      // for a stale phrase on reopen; keep the existing stale times otherwise.
      staleTime: effectiveSearchQuery
        ? 0
        : isUnifiedDiscoveryPreset(preset)
          ? 30_000
          : 5 * 60 * 1000,
    },
  });

  const { data: availablePagesData } = useDiscoverAvailablePages({
    page,
    sortBy,
    sortDirection: sortDirection || 'desc',
    timeframe,
    filters,
    preset,
    pageDiscovery: true,
    highestKnownPage,
    totalCount: data?.totalCount,
    searchQuery: effectiveSearchQuery,
  });

  useEffect(() => {
    if (availablePagesData) {
      setHighestKnownPage(availablePagesData.availablePages);
    }
  }, [availablePagesData]);

  const intersectionRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const intersection = useIntersection(intersectionRef, {
    rootMargin: '0px 0px 50px 0px',
  });

  const handleScrollBarMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const scrollContainer = scrollContainerRef.current;
    const content = contentRef.current;
    const scrollbar = scrollbarRef.current;
    const thumb = thumbRef.current;

    if (!scrollContainer || !content || !scrollbar || !thumb) {
      return;
    }

    const startX = e.clientX;

    // dragging action
    const initialScrollLeft = scrollContainer.scrollLeft;
    // we assume the user will not resize the window while dragging
    const thumbWidth = thumb.offsetWidth;
    const maxThumbLeft = scrollbar.clientWidth - thumbWidth;

    thumb.style.cursor = 'grabbing';

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const maxScroll = content.scrollWidth - scrollContainer.clientWidth;
      const scrollDelta = (deltaX / maxThumbLeft) * maxScroll;
      scrollContainer.scrollLeft = Math.max(
        0,
        Math.min(initialScrollLeft + scrollDelta, maxScroll),
      );
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      document.body.style.userSelect = '';
      thumb.style.cursor = 'grab';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // ensure the user does not accidentally select text
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    if (!isFetching && page > 1) {
      prefetch({
        page: page - 1,
        sortBy: sortBy || 'trendingScore',
        sortDirection: sortDirection || 'desc',
        timeframe,
        filters,
        preset,
        pageDiscovery: false,
        searchQuery: effectiveSearchQuery,
      });
    }
    if (!isFetching && availablePagesData && page < availablePagesData.availablePages) {
      prefetch({
        page: page + 1,
        sortBy: sortBy || 'trendingScore',
        sortDirection: sortDirection || 'desc',
        timeframe,
        filters,
        preset,
        pageDiscovery: false,
        searchQuery: effectiveSearchQuery,
      });
    }
  }, [
    isFetching,
    availablePagesData,
    page,
    sortBy,
    sortDirection,
    timeframe,
    filters,
    preset,
    router.query.shouldError,
    prefetch,
    effectiveSearchQuery,
  ]);

  // if filters, sortings, or search change, we reset pagination
  useEffect(() => setHighestKnownPage(0), [filters, sortBy, sortDirection, effectiveSearchQuery]);

  // Reset the dedupe ref when the user clears the input so re-typing the
  // same term in the same session re-fires.
  useEffect(() => {
    if (!effectiveSearchQuery) {
      lastFiredSearchKeyRef.current = null;
    }
  }, [effectiveSearchQuery]);

  // Stable, sorted representation of the explicit network filter so toggling
  // between specific chains is treated as a new search by the dedupe key
  // below. Empty string means no explicit filter.
  const explicitNetworksKey = useMemo(
    () => [...filtersProp.networks].sort((a, b) => a - b).join(','),
    [filtersProp.networks],
  );

  // Fires `discovery_table_search_initiated` once per completed, debounced
  // search. Waits for the response so `result_count` matches the query, and
  // skips error responses so outages do not pollute zero-result metrics.
  useEffect(() => {
    if (isFetching || !effectiveSearchQuery || data == null || data.didError) {
      return;
    }

    const presetForEvent = preset ?? 'trending';
    const key = `${presetForEvent}::${explicitNetworksKey}::${effectiveSearchQuery}`;
    if (lastFiredSearchKeyRef.current === key) {
      return;
    }

    ampli.discoveryTableSearchInitiated({
      discovery_preset: presetForEvent,
      search_term: effectiveSearchQuery,
      result_count: data.totalCount ?? data.result.length,
      // Reflect explicit user intent only; the implicit SUPPORTED_CHAIN_IDS
      // expansion does not count as a network filter.
      had_network_filter: explicitNetworksKey.length > 0,
    });
    lastFiredSearchKeyRef.current = key;
  }, [isFetching, effectiveSearchQuery, data, preset, explicitNetworksKey]);

  type TableRowType = UnifiedDiscoveryResultItem | Record<string, never>;

  const columns = useMemo(() => {
    const tokenColumnSize = isTabletUp ? 320 : 240;
    const category = preset ?? 'trending';
    const unifiedColumns =
      preset === 'commodities'
        ? UNIFIED_COMMODITIES_COLUMNS
        : preset === 'stocks'
          ? UNIFIED_STOCKS_COLUMNS
          : preset === 'currencies'
            ? UNIFIED_CURRENCIES_COLUMNS
            : UNIFIED_POPULAR_COLUMNS;
    const baseColumns = (columnsOverride ??
      (isUnifiedDiscoveryPreset(preset)
        ? unifiedColumns
        : CATEGORY_COLUMNS_MAP[category])) as ColumnDef<TableRowType>[];
    const sizedColumns = columnsOverride
      ? baseColumns
      : baseColumns.map((column) =>
          column.id === 'token' ? { ...column, size: tokenColumnSize } : column,
        );

    if (!isFetching) {
      return sizedColumns;
    }

    return sizedColumns.map((column) => ({
      ...column,
      cell: () => {
        if (column.id === 'token') {
          return (
            <div className="flex items-center">
              <div className="relative flex p-[3px]">
                <AvatarSkeleton size={32} />
                <AvatarSkeleton
                  size={15}
                  className="absolute bottom-[1px] right-[-1px] border-2 border-special-background-card"
                  radius={chainAvatarRadius}
                />
              </div>
              <div className="ml-3 flex flex-col gap-1">
                <TextSkeleton height={14} width={115} />
                <TextSkeleton height={13} width={42} />
              </div>
            </div>
          );
        }

        return <TextSkeleton className="w-1/2" />;
      },
    }));
  }, [isFetching, isTabletUp, preset, columnsOverride]);

  // Rows are now handled server-side for popular preset
  // The API returns a mixed array of Asset and DiscoveryTokenResult
  const rows = useMemo(() => {
    if (isFetching) {
      return new Array(50).fill({});
    }
    return data?.result ?? [];
  }, [isFetching, data?.result]);

  const tokenResults = useMemo(
    () =>
      data?.result.reduce<DiscoveryTokenResult[]>(
        (current, item) => (isAsset(item) ? current.concat(item.tokens) : current.concat(item)),
        [],
      ),
    [data?.result],
  );

  // Extract all unique tokens without liquidity score from rows to batch-fetch liquidity scores
  const allTokensForScores = useMemo(() => {
    if (isFetching || !tokenResults || isNeonDbTokensBackedPreset(preset)) return undefined;

    const tokenSet = new Map<string, { address: string; chainId: number }>();

    for (const item of tokenResults) {
      const { token, liquidityScore } = item;
      const key = tokenKey(token.address, token.chainId);

      if (!tokenSet.has(key) && liquidityScore == null) {
        tokenSet.set(key, { address: token.address, chainId: token.chainId });
      }
    }

    return Array.from(tokenSet.values());
  }, [isFetching, tokenResults, preset]);

  const { data: liquidityScoresData, isLoading: isLiquidityScoresLoading } =
    useLiquidityScore(allTokensForScores);

  // Resolve token results for blocked-token and Zora checks (avoids N+1 per-row
  // fetches). Each token is its own entity query; the shared token loader
  // coalesces the concurrent fetches into batched requests.

  // Unique (address, chainId) pairs for the current page of discovery results.
  const tokenPairs = useMemo(() => {
    if (isFetching || !tokenResults || isNeonDbTokensBackedPreset(preset)) {
      return [] as { address: string; chainId: number; key: string }[];
    }

    const seen = new Set<string>();
    const pairs: { address: string; chainId: number; key: string }[] = [];
    for (const item of tokenResults) {
      const { token } = item;
      const key = tokenKey(token.address, token.chainId);
      if (seen.has(key)) continue;
      seen.add(key);
      pairs.push({ address: token.address, chainId: token.chainId, key });
    }
    return pairs;
  }, [isFetching, tokenResults, preset]);

  const queries = useQueries({
    queries: tokenPairs.map(({ address, chainId }) => tokenEntityQueryOptions(address, chainId)),
  });

  const dataSignature = queries
    .map((query) => (query.data ? tokenKey(query.data.address, query.data.chainId) : '-'))
    .join('|');

  const tokenResultsMap = useMemo(() => {
    const resultsMap = new Map<string, TokenResult>();
    tokenPairs.forEach(({ key }, index) => {
      const token = queries[index]?.data;
      if (token) resultsMap.set(key, token);
    });
    return resultsMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenPairs, dataSignature]);

  // Build a lookup map keyed by "normalizedAddress:chainId" for O(1) access in cells
  const liquidityScores = useMemo(() => {
    const map = new Map<string, number | undefined>();

    if (tokenResults == null) {
      return map;
    }

    for (const item of tokenResults) {
      const { token, liquidityScore } = item;
      const key = tokenKey(token.address, token.chainId);
      const score =
        liquidityScore ??
        liquidityScoresData?.data.find((t) => tokenKey(t.address, t.chainId) === key)?.score;
      map.set(key, score);
    }

    return map;
  }, [liquidityScoresData, tokenResults]);

  const table = useReactTable<TableRowType>({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: (updaterOrValue) => {
      // extract new values
      const { id, desc } =
        typeof updaterOrValue === 'function'
          ? updaterOrValue([{ id: sortBy, desc: sortDirection === 'desc' }])[0]
          : updaterOrValue[0];

      setSorting({
        sortBy: id as DiscoveryParametersSerialized['sortBy'],
        sortDirection: desc ? 'desc' : 'asc',
      });
      ampli.homepageSortChanged({
        sort_column: id,
        sort_direction: desc ? 'desc' : 'asc',
      });
    },
    meta: {
      preset,
      timeframe,
      liquidityScores,
      isLiquidityScoresLoading,
      tokenResultsMap,
      formatFiat,
      currencySymbol,
      convertFromUsd,
    },
    manualSorting: true,
    manualPagination: true,
    state: {
      sorting: [{ id: sortBy, desc: sortDirection === 'desc' }],
      pagination: pageInfo,
    },
  });

  const showPagination = availablePagesData && availablePagesData.availablePages > 1;
  const searchPlaceholder = getSearchPlaceholder(preset);

  return (
    <div>
      {data?.didError && (
        <div className="flex items-center justify-start h-full max-w-[min(calc(100vw-36px),1528px)] desktop:max-w-[min(calc(100vw-200px),1528px)] w-full mx-auto px-4 mb-4">
          <div className="flex items-center gap-2 text-token-gray-800">
            <AlertIcon className="size-4" />
            <p className={fontStyles({ variant: 'BODY_SM', class: ['text-token-gray-800'] })}>
              We are experiencing some issues with our data provider. The displayed data might be
              outdated.
            </p>
          </div>
        </div>
      )}
      <div className="relative">
        <div className="max-w-[min(calc(100vw-36px),1528px)] desktop:max-w-[min(calc(100vw-200px),1528px)] w-full mx-auto">
          {preset === 'stocks' && showStocksAvailabilityBanner && (
            <div className="flex items-center justify-center gap-2 border-l border-r border-t border-solid border-token-alphalight-300 bg-token-alphalight-100 px-8 pt-3 pb-7 -mb-4 rounded-t-2xl">
              <GlobeIcon className="h-4 w-4 text-token-gray-700" />
              <p className={fontStyles({ variant: 'LABEL_SM', class: ['text-token-gray-800'] })}>
                Tokenized stocks are not available for trading in the US
              </p>
            </div>
          )}
        </div>
        {!disableStickyHeader && (
          <>
            <div ref={stickyHeaderTriggerRef} className="h-px" aria-hidden={true} />
            <div
              ref={stickyHeaderRef}
              data-visible="false"
              aria-hidden={true}
              className={classNames(
                'fixed z-[90] left-1/2 -translate-x-1/2 w-full overflow-hidden',
                'max-w-[min(calc(100vw-36px),1528px)] desktop:max-w-[min(calc(100vw-200px),1528px)]',
                'bg-special-background-app',
                'data-[visible=true]:block data-[visible=false]:hidden',
              )}
              style={{ top: stickyHeaderTopOffset }}
            >
              <div
                ref={stickyHeaderScrollContainerRef}
                data-did-scroll="false"
                className="overflow-x-auto no-scrollbar group/wrapper"
              >
                <table className="w-full px-11 bg-special-background-card table-fixed">
                  <DiscoveryTableHeader
                    table={table}
                    isSticky={true}
                    searchInputValue={searchInputValue}
                    onSearchInputChange={setSearchInputValue}
                    onClearSearch={handleClearSearch}
                    searchInputRef={stickySearchInputRef}
                    searchPlaceholder={searchPlaceholder}
                  />
                </table>
              </div>
            </div>
          </>
        )}
        <div
          className="border border-token-alphalight-200 no-scrollbar group/wrapper max-w-[min(calc(100vw-36px),1528px)] desktop:max-w-[min(calc(100vw-200px),1528px)] w-full mx-auto rounded-lg overflow-x-auto bg-special-background-app relative"
          ref={scrollContainerRef}
          data-did-scroll="false"
        >
          <div ref={contentRef} className="relative">
            <table className="w-full px-11 bg-special-background-card ">
              <DiscoveryTableHeader
                table={table}
                isSticky={false}
                searchInputValue={searchInputValue}
                onSearchInputChange={setSearchInputValue}
                onClearSearch={handleClearSearch}
                searchInputRef={regularSearchInputRef}
                searchPlaceholder={searchPlaceholder}
              />
              <tbody>
                {!error &&
                  table.getRowModel().rows.map((row) => {
                    const item = row.original as DiscoveryTokenResult | Asset;
                    const isLastRow = row.id === String(table.getRowModel().rows.length - 1);

                    // Check if this is an asset with multiple tokens - use UnifiedTokenRow for expandable display
                    // Only render UnifiedTokenRow for assets with 2+ tokens
                    // Single-token assets fall through to regular row rendering
                    if (
                      isUnifiedDiscoveryPreset(preset) &&
                      isAsset(item) &&
                      item.tokens.length > 1
                    ) {
                      return (
                        <UnifiedTokenRow
                          key={row.id}
                          asset={item}
                          row={row}
                          preset={preset}
                          isLastRow={isLastRow}
                          showPagination={showPagination}
                        />
                      );
                    }

                    // Regular token row - used for standalone tokens and single-token assets
                    // For single-token assets, extract the token from the asset
                    const tokenData: DiscoveryTokenResult | null =
                      'token' in item
                        ? (item as DiscoveryTokenResult)
                        : isAsset(item) && item.tokens[0]
                          ? item.tokens[0]
                          : null;

                    const handleOpenInNewTab = () => {
                      if (tokenData) {
                        window.open(
                          `${window.location.origin}/tokens/${NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[tokenData.token.chainId]}/${tokenData.token.address}`,
                          '_blank',
                          'noopener,noreferrer',
                        );
                      }
                    };
                    const handleRowClick = ({ target }: React.KeyboardEvent | React.MouseEvent) => {
                      const isLink = target instanceof HTMLElement && target.closest('a') != null;

                      if (tokenData) {
                        ampli.homepageTokenClicked({
                          token_address: tokenData.token.address,
                          token_chain_id: tokenData.token.chainId,
                          token_chain_name:
                            NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[tokenData.token.chainId],
                          token_symbol: tokenData.token.symbol,
                        });
                        // Push the new route programmatically only if the event target is not a link
                        if (!isLink) {
                          router.push(buildTokenPageRoute(tokenData.token));
                        }
                      }
                    };

                    return (
                      <tr
                        key={row.id}
                        className="hover:bg-token-alphalight-200 cursor-pointer relative group"
                        {...addMetaClickHandlers({
                          onMetaClick: handleOpenInNewTab,
                          onClick: handleRowClick,
                        })}
                        role="link"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleRowClick(e);
                          }
                        }}
                        aria-label={`View ${tokenData ? tokenData.token.symbol : ''} token details`}
                      >
                        {row.getVisibleCells().map((cell) => {
                          return (
                            <td
                              className={fontStyles({
                                variant: 'BODY_MD',
                                class: [
                                  'p-0 border-solid border-token-gray-300 h-16 px-2 first:sticky first:z-10',
                                  columnsOverride
                                    ? 'first:pl-4'
                                    : isUnifiedDiscoveryPreset(preset)
                                      ? 'first:pl-[34px]'
                                      : 'tablet:first:pl-[34px]',
                                  ...STICKY_FIRST_CELL_BG_CLASSES,
                                  'group-data-[did-scroll=true]/wrapper:first:after:shadow-10 group-data-[did-scroll=true]/wrapper:first:after:absolute',
                                  'group-data-[did-scroll=true]/wrapper:first:after:inset-0',
                                  'left-0 tablet:last:pr-11 last:pr-4 whitespace-nowrap align-middle',
                                  !isLastRow || showPagination ? 'border-b' : '',
                                ],
                              })}
                              style={{ width: cell.column.getSize() }}
                              key={cell.id}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {data?.result.length === 0 && !isFetching && !error && (
            <EmptyState hasNetworkFilter={!!filters.networks} searchQuery={effectiveSearchQuery} />
          )}
          {error && !isFetching && <EmptyState isError />}
          {showPagination && (
            <div className="sticky left-0 right-0">
              <div className="flex justify-center items-center py-3.5">
                <Pagination
                  currentPage={page}
                  lastPage={availablePagesData.availablePages}
                  onPageChange={(page) => setPage(page)}
                  onFirst={() => setPage(1)}
                  onPrevious={() => setPage(page - 1)}
                  onNext={() => setPage(page + 1)}
                  onLast={() => setPage(availablePagesData.availablePages)}
                  pageSection={(lastPage) => {
                    const pageSection = [];
                    const start = Math.max(1, Math.floor(page - maxPageSectionSize / 2));
                    const end = Math.min(lastPage, start + maxPageSectionSize - 1);
                    for (let i = start; i <= end; i++) {
                      pageSection.push(i);
                    }
                    return pageSection;
                  }}
                />
              </div>
            </div>
          )}
        </div>
        <div
          data-scrollable={isScrollable}
          data-sticky={intersection?.isIntersecting ?? false}
          className={`z-100 hidden tablet:data-[scrollable=true]:block fixed data-[sticky=true]:absolute bottom-4 
            data-[sticky=true]:bottom-[60px] h-2 max-w-[calc(100vw-36px)] desktop:max-w-[calc(100vw-216px)] w-full left-1/2 -translate-x-1/2
             transition-transform duration-200`}
          ref={scrollbarRef}
        >
          <div
            ref={thumbRef}
            className="absolute top-0 left-0 h-full bg-token-gray-600 cursor-grab rounded-full"
            onMouseDown={handleScrollBarMouseDown}
          />
        </div>
        <div ref={intersectionRef} />
      </div>
    </div>
  );
}
